import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { amigoAPI } from '../services/api';
import { Send, Trash2, Bot, User, AlertTriangle, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const ChatMessage = ({ message, isUser }) => {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex items-start gap-3 max-w-[80%] ${isUser ? 'flex-row-reverse' : ''}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-primary/20' : 'bg-gradient-to-br from-purple-500 to-pink-500'
        }`}>
          {isUser ? (
            <User className="w-5 h-5 text-primary" />
          ) : (
            <Bot className="w-5 h-5 text-white" />
          )}
        </div>
        <div className={`rounded-2xl px-4 py-3 ${
          isUser 
            ? 'bg-primary text-white rounded-br-none' 
            : 'bg-card border border-border text-foreground rounded-bl-none'
        }`}>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
          <p className={`text-xs mt-2 ${isUser ? 'text-white/70' : 'text-muted-foreground'}`}>
            {new Date(message.created_at).toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="flex justify-start mb-4">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
        <Bot className="w-5 h-5 text-white" />
      </div>
      <div className="bg-card border border-border rounded-2xl rounded-bl-none px-4 py-3">
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  </div>
);

const NoAccessView = () => (
  <div className="min-h-screen bg-background flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-card rounded-2xl p-8 border border-border text-center">
      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
        <Lock className="w-10 h-10 text-primary" />
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
        Unlock Amigo AI Companion
      </h2>
      <p className="text-muted-foreground mb-6">
        Amigo is your personal AI companion for mental wellness support. Subscribe to any plan to start chatting with Amigo.
      </p>
      <div className="space-y-3 text-left mb-6 bg-muted/50 rounded-xl p-4">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Sparkles className="w-4 h-4 text-primary" />
          <span>24/7 supportive conversations</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Sparkles className="w-4 h-4 text-primary" />
          <span>Personalized emotional support</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Sparkles className="w-4 h-4 text-primary" />
          <span>Mood-aware responses</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Sparkles className="w-4 h-4 text-primary" />
          <span>Coping strategies & exercises</span>
        </div>
      </div>
      <Link 
        to="/profile" 
        className="inline-block w-full py-3 rounded-full bg-primary text-white font-semibold hover:bg-primary/90"
        data-testid="subscribe-btn"
      >
        View Subscription Plans
      </Link>
    </div>
  </div>
);

export const Amigo = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      loadHistory();
    }
  }, [hasAccess]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkAccess = async () => {
    try {
      const response = await amigoAPI.checkAccess();
      setHasAccess(response.data.has_access);
    } catch (error) {
      console.error('Failed to check access:', error);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await amigoAPI.getHistory();
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message immediately
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    setLoading(true);
    
    try {
      const response = await amigoAPI.sendMessage(userMessage);
      
      // Add AI response
      const aiMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: response.data.response,
        created_at: new Date().toISOString(),
        is_urgent: response.data.is_urgent
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Show urgent warning if detected
      if (response.data.is_urgent) {
        toast.warning(
          'We noticed you might be going through a difficult time. Please remember help is available - Emergency: 112, Crisis Line: 788789882',
          { duration: 10000 }
        );
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      // Remove the temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear your chat history? This cannot be undone.')) {
      return;
    }
    
    try {
      await amigoAPI.clearHistory();
      setMessages([]);
      toast.success('Chat history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="amigo-page">
      {/* Header */}
      <div className="bg-card border-b border-border px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Amigo
              </h1>
              <p className="text-sm text-muted-foreground">Your AI wellness companion</p>
            </div>
          </div>
          <button
            onClick={handleClearHistory}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Clear chat history"
            data-testid="clear-history-btn"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6">
                <Bot className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Hi {user?.full_name?.split(' ')[0] || 'there'}! I'm Amigo 💜
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                I'm here to listen, support, and chat with you about anything on your mind. 
                How are you feeling today?
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {['I need someone to talk to', 'Feeling a bit stressed', 'Just checking in', 'Need some motivation'].map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => setInputMessage(prompt)}
                    className="px-4 py-2 rounded-full bg-muted text-foreground text-sm hover:bg-primary/10 hover:text-primary transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage 
                  key={message.id} 
                  message={message} 
                  isUser={message.role === 'user'} 
                />
              ))}
              {loading && <TypingIndicator />}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-card border-t border-border px-4 py-4">
        <form onSubmit={handleSend} className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-3 rounded-full bg-muted border border-border text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={loading}
              data-testid="amigo-input"
            />
            <button
              type="submit"
              disabled={loading || !inputMessage.trim()}
              className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="amigo-send-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-3">
            Amigo is an AI companion and not a replacement for professional help. 
            <span className="text-destructive ml-1">Emergency: 112 | Crisis: 788789882</span>
          </p>
        </form>
      </div>
    </div>
  );
};
